/**
 * Follow API Service
 * Handles all follow-related API calls
 */

import { PAGINATION } from "@/constants/constants"
import type { ProfilePhoto } from "@/features/auth/store/authStore"
import { apiClient } from "@/lib/api-client"

/**
 * User interface for follow operations
 */
export interface FollowUser {
  id: string
  name: string | null
  username: string | null
  profilePictureUrl: string | null
  bio: string | null
  city: string | null
  profileVisibility?: "public" | "private"
  isFollowing?: boolean
  isPending?: boolean
  profile_photos?: ProfilePhoto[]
}

/**
 * Paginated followers/following response
 */
export interface FollowListResponse {
  followers?: FollowItem[]
  following?: FollowItem[]
  total: number
  page: number
  pages: number
}

/**
 * Follow item in list
 */
export interface FollowItem {
  id: string
  followerId: string
  followingId: string
  createdAt: Date
  user: FollowUser
}

/**
 * Follow request interface
 */
export interface FollowRequest {
  id: string
  requesterId: string
  recipientId: string
  status: "pending" | "accepted" | "rejected"
  requestedAt: Date
  user: FollowUser
}

/**
 * Follow status interface
 */
export interface FollowStatus {
  isFollowing: boolean
  isPending: boolean
}

/**
 * Follow a user (or send follow request for private profiles)
 */
export async function followUser(
  userId: string,
): Promise<{ success: boolean; isDirect: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; isDirect: boolean; message: string }
    message: string
  }>(`/api/follow/${userId}`)

  return response.data.data
}

/**
 * Unfollow a user (or cancel pending follow request)
 */
export async function unfollowUser(userId: string): Promise<{ message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: { message: string }
    message: string
  }>(`/api/follow/${userId}`)

  return response.data.data
}

/**
 * Get followers list for a user
 */
export async function getFollowers(
  userId: string,
  page = PAGINATION.DEFAULT_PAGE,
  limit = PAGINATION.DEFAULT_LIMIT,
  search?: string,
): Promise<FollowListResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: FollowListResponse
    message: string
  }>(`/api/users/${userId}/followers`, { params: { page, limit, search } })

  return response.data.data
}

/**
 * Get following list for a user
 */
export async function getFollowing(
  userId: string,
  page = PAGINATION.DEFAULT_PAGE,
  limit = PAGINATION.DEFAULT_LIMIT,
  search?: string,
): Promise<FollowListResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: FollowListResponse
    message: string
  }>(`/api/users/${userId}/following`, { params: { page, limit, search } })

  return response.data.data
}

/**
 * Get pending follow requests for current user
 */
export async function getPendingRequests(): Promise<{ requests: FollowRequest[] }> {
  const response = await apiClient.get<{
    success: boolean
    data: { requests: FollowRequest[] }
    message: string
  }>("/api/follow/requests/pending")

  return response.data.data
}

/**
 * Accept a follow request
 */
export async function acceptFollowRequest(requestId: string): Promise<FollowRequest> {
  const response = await apiClient.post<{
    success: boolean
    data: { followRequest: FollowRequest }
    message: string
  }>(`/api/follow/requests/${requestId}/accept`)

  return response.data.data.followRequest
}

/**
 * Reject a follow request
 */
export async function rejectFollowRequest(requestId: string): Promise<FollowRequest> {
  const response = await apiClient.post<{
    success: boolean
    data: { followRequest: FollowRequest }
    message: string
  }>(`/api/follow/requests/${requestId}/reject`)

  return response.data.data.followRequest
}

/**
 * Check follow status between current user and target user
 * @deprecated This function is kept for backward compatibility.
 * Follow status should now be included in the profile API response.
 * Use this only when follow status is not already available from profile data.
 */
export async function checkFollowStatus(_userId: string): Promise<FollowStatus> {
  // Note: This endpoint has been removed from the backend.
  // This function now throws an error to force migration to the new pattern.
  throw new Error(
    "checkFollowStatus is deprecated. Follow status should be included in the profile API response. " +
      "If you're seeing this error, you're using FollowButton without providing initialStatus prop.",
  )
}

/**
 * Batch check follow status for multiple user IDs.
 * Returns a map of userId -> boolean (isFollowing).
 */
export async function batchFollowStatus(userIds: string[]): Promise<Record<string, boolean>> {
  if (userIds.length === 0) return {}
  const response = await apiClient.get<{
    success: boolean
    data: { statuses: Record<string, boolean> }
  }>(`/api/follow/status?ids=${userIds.join(",")}`)
  return response.data.data.statuses
}
